<template lang="pug">
NuxtLink.venue-card-wrapper(:to="localePath(link)")
	.img-wrap
		nuxt-img.venue-image(:src="image")
		.venue-opening-hours(v-if="openingHours")
			.indicator(:style="{background: indicatorColor}")
			span.opening-hours {{ openingHours }}
	.venue-info
		span.venue-name {{ props.venue.name }}
		span.venue-address {{ props.venue.address }}
		span.venue-description {{ props.venue.description }}
</template>

<script setup lang="ts">
const useImage = useGetImageSize()
const localePath = useLocalePath()
const { t } = useI18n()

const props = defineProps({
	venue: {
		type: Object,
		required: true
	},
	date: {
		type: Object,
		required: true
	}
})

const image = useImage(props.venue.image, 'medium', 'song', true)
const weekday = props.date.weekday
const currentTime = props.date.currentTime
const indicatorColor = ref('')
const link = `/karaoke-near-me/${props.venue.country_code.toLowerCase()}/${props.venue.city_slug}/${props.venue.slug}/${props.venue.hash}`

// Map the weekday to the correct index in the array to check if the venue is open today.
// Then check if the venue is open now, if it will open later today or if it is closed. Do nothing if the venue has no opening hours.

const openingHours = computed(() => {
	const daysOpen = props.venue.opening_hours?.map((weekday: any) => weekday.weekday)
	const index = daysOpen.indexOf(weekday)
	const openToday = index !== -1
	if (props.venue.opening_hours.length < 1) {
		return null
	} else if (openToday && props.venue.opening_hours[index].from_hour <= currentTime) {
		indicatorColor.value = '#12B961'
		return t('venue.openNow')
	} else if (openToday && props.venue.opening_hours[index].from_hour >= currentTime) {
		indicatorColor.value = '#EECD44'
		return `${t('venue.open')} ${props.venue.opening_hours[index].from_hour.slice(0, 5)}–${props.venue.opening_hours[index].to_hour.slice(0, 5)}`
	} else if (!openToday) {
		indicatorColor.value = '#D33F2C'
		return t('venue.closed')
	}
	return ''
})
</script>

<style lang="sass" scoped>
.venue-card-wrapper
	margin: 0 0 $spacing-40 0
	display: flex
	flex-direction: column
	position: relative
	&:hover
		.venue-opening-hours span, .venue-description
			color: #ccc
		.venue-name
			text-decoration: underline

.venue-info
	overflow-wrap: break-word
	overflow: hidden
	white-space: nowrap
	text-overflow: ellipsis

.venue-name
	color: #fff
	display: block
	@include fontSize(xl)
	@include font(basier, bold)
	padding-top: $spacing-16
	overflow: hidden
	white-space: nowrap
	text-overflow: ellipsis

.venue-address
	color: $color-grey-50

.venue-description
	@include fontSize(s)
	white-space: normal
	overflow: hidden
	text-overflow: ellipsis
	display: block
	display: -webkit-box
	-webkit-line-clamp: 2
	-webkit-box-orient: vertical

.venue-image
	position: relative
	aspect-ratio: 2 / 1
	object-fit: cover
	z-index: -5

.venue-opening-hours
	max-width: 100%
	height: 32px
	border-radius: 100px
	background: #0000004a
	box-shadow: 0px 4px 4px 0px rgba(0, 0, 0, 0.15)
	backdrop-filter: blur(15px)
	display: flex
	flex-direction: row
	align-items: center
	padding: $spacing-8 $spacing-16 $spacing-8 $spacing-8
	gap: $spacing-8
	flex-wrap: nowrap
	position: absolute
	bottom: $spacing-16
	left: $spacing-8

.opening-hours
	@include font(basier, bold)
	@include fontSize(xxs)
	text-transform: uppercase
	text-shadow: 0 0 16px $transparent-black-56

.indicator
	height: 16px
	width: 16px
	border-radius: 100%
	display: inline-block
	filter: drop-shadow(0px 0px 8px rgba(0, 0, 0, 0.25))
	z-index: 1

.img-wrap
	position: relative
	img
		width: 100%
</style>
